/**
 * @fileoverview Fields Settings — Field Presets Manager
 *
 * Purpose: Let users manage grouped field presets per canonical category.
 * Each preset is a named set of field values (e.g. "Morgan Liquidsworth"
 * practitioner preset). Supports multiple presets per category, org defaults,
 * and per-user favourites.
 *
 * URL: /dashboard/settings/fields
 */

import { createFileRoute } from "@tanstack/react-router";
import { convexQuery, useConvexMutation } from "@convex-dev/react-query";
import { api } from "~/convex/_generated/api";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useActiveOrg } from "@/utils/org-context";
import { DayOneContactDefaultCard } from "./-ui.day-one-contact-default";
import { TitleByAppointmentType } from "./-ui.title-by-appointment-type";
import { APPOINTMENT_TYPE_LABELS } from "@/components/pre-appointment/constants";
import type { AppointmentType } from "@cvx/jobs/schema";
import { Input, Badge, Spinner, Button, Switch } from "@/ui/primitives";
import { useCallback, useMemo, useRef, useState } from "react";
import { useAutosave, AutosaveStatus } from "@/utils/forms";
import {
  User,
  Building2,
  Phone,
  PenTool,
  FileText,
  ShieldCheck,
  Plus,
  PlusCircle,
  Star,
  Trash2,
  ChevronDown,
  ChevronRight,
  Shield,
  Check,
  Loader2,
  Lock,
  Pencil,
  ShieldOff,
  X,
  Info,
} from "lucide-react";
import { cn } from "@/utils/misc";
import type { Doc, Id } from "~/convex/_generated/dataModel";
import { toast } from "sonner";
import {
  FIELD_CATEGORIES,
  PER_CASE_CATEGORIES,
  DEFAULT_CATEGORIES,
  CROSS_CATEGORY_KEYS_BY_CATEGORY,
  CANONICAL_FIELDS_MAP,
  type FieldCategory,
  type CanonicalField,
} from "~/convex/settings/precedents/canonicalFields";
import { TITLE_KEYS } from "~/convex/lib/titleScopes";

/**
 * Signatory/practitioner title keys are managed by the appointment-type
 * titles table rather than shown as plain fields, so the field grid and its
 * "N/M filled" count exclude them to avoid duplicating the concept.
 */
const TITLE_FIELD_KEYS: ReadonlySet<string> = new Set(TITLE_KEYS);
const isTitleField = (field: CanonicalField): boolean =>
  TITLE_FIELD_KEYS.has(field.key);
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogTrigger,
} from "@/ui/composed/overlays/dialog";
import { Label } from "@/ui/primitives/label";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/ui/composed/overlays";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/ui/composed/overlays/tooltip";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/ui/composed/sections/tabs";

export const Route = createFileRoute(
  "/_app/_auth/dashboard/_layout/settings/fields",
)({
  component: FieldsSettings,
  beforeLoad: () => ({
    title: "Fields",
    headerTitle: "Settings",
  }),
});

const GROUP_CARD_CLASS =
  "rounded-xl bg-background shadow-[0_1px_3px_rgba(0,0,0,0.08),0_1px_2px_rgba(0,0,0,0.06)] hover:shadow-[0_4px_12px_rgba(0,0,0,0.1),0_2px_4px_rgba(0,0,0,0.06)] transition-shadow duration-200 overflow-hidden";

const CATEGORY_ICONS: Record<
  string,
  React.ComponentType<{ className?: string }>
> = {
  practitioner: User,
  firm: Building2,
  contact: Phone,
  signature: PenTool,
  document: FileText,
  gallagher: ShieldCheck,
};

/**
 * Fields in a defaultable category that are nonetheless filled in for the
 * practitioner — by the date the document is generated, or by the job record.
 * A firm-wide default would be meaningless, so they're hidden from the preset
 * editor.
 */
const AUTO_ASSIGNED_FIELDS = new Set([
  "letter_date",
  "signature_date",
  "submission_date",
  "job_code",
]);

const defaultableCategories = FIELD_CATEGORIES.filter((c) =>
  DEFAULT_CATEGORIES.has(c.id),
);
const perCaseCategories = FIELD_CATEGORIES.filter((c) =>
  PER_CASE_CATEGORIES.has(c.id),
);

// ---------------------------------------------------------------------------
// InfoTip — small (i) icon with a tooltip explaining a concept
// ---------------------------------------------------------------------------

function InfoTip({
  children,
  side = "top",
  align = "start",
  className,
}: {
  children: React.ReactNode;
  side?: "top" | "right" | "bottom" | "left";
  align?: "start" | "center" | "end";
  className?: string;
}) {
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            tabIndex={-1}
            aria-label="More information"
            onClick={(e) => e.stopPropagation()}
            className={cn(
              "rounded-full p-0.5 text-muted-foreground/60 transition hover:text-muted-foreground focus:outline-none focus-visible:outline-none",
              className,
            )}
          >
            <Info className="size-3.5" />
          </button>
        </TooltipTrigger>
        <TooltipContent
          side={side}
          align={align}
          className="max-w-xs text-xs leading-relaxed"
        >
          {children}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

// Shared "how custom fields work" tooltip body — Category → Fields → Presets.
function CustomFieldsHelp() {
  return (
    <>
      <p className="font-medium">How custom fields work</p>
      <p className="mt-1.5">
        <span className="font-medium">Category</span> — a named group of related
        fields (e.g. &ldquo;Vehicle Search&rdquo;).
      </p>
      <p className="mt-1">
        <span className="font-medium">Fields</span> — the individual pieces of
        information inside a category. You can also create fields straight from
        a template&apos;s Insert Field menu.
      </p>
      <p className="mt-1">
        <span className="font-medium">Presets</span> — a saved set of values for
        a category. A field only auto-fills a document once it has a value in a
        preset (or is filled in per job).
      </p>
    </>
  );
}

// ---------------------------------------------------------------------------
// SectionHeading — group divider used on the "All" tab
// ---------------------------------------------------------------------------

function SectionHeading({
  icon: Icon,
  title,
  subtitle,
}: {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  subtitle: string;
}) {
  return (
    <div className="flex items-center gap-3 pt-2 first:pt-0">
      <div className="flex size-7 items-center justify-center rounded-lg bg-primary/5">
        <Icon className="size-3.5 text-primary/60" />
      </div>
      <div className="min-w-0">
        <h4 className="text-sm font-semibold text-primary">{title}</h4>
        <p className="text-xs text-primary/40">{subtitle}</p>
      </div>
      <div className="h-px flex-1 bg-border/60" />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

function FieldsSettings() {
  const { orgId } = useActiveOrg();
  const typedOrgId = orgId as Id<"organizations">;

  const { data: presets, isLoading } = useQuery({
    ...convexQuery(api.settings.precedents.presets.listByOrg, {
      orgId: typedOrgId,
    }),
    enabled: !!orgId,
  });

  const { data: favouriteIds } = useQuery({
    ...convexQuery(api.settings.precedents.favourites.listByUser, {
      orgId: typedOrgId,
    }),
    enabled: !!orgId,
  });

  const { data: customFieldDefs } = useQuery({
    ...convexQuery(api.settings.precedents.customFields.listByOrg, {
      orgId: typedOrgId,
    }),
    enabled: !!orgId,
  });

  const favouriteSet = useMemo(
    () => new Set(favouriteIds ?? []),
    [favouriteIds],
  );

  const presetsByCategory = useMemo(() => {
    const map = new Map<string, typeof presets>();
    if (!presets) return map;
    for (const p of presets) {
      const list = map.get(p.categoryId) ?? [];
      list.push(p);
      map.set(p.categoryId, list);
    }
    return map;
  }, [presets]);

  const customCategories = useMemo(() => {
    if (!customFieldDefs) return [];
    const map = new Map<
      string,
      { categoryId: string; categoryLabel: string; fields: CanonicalField[] }
    >();
    for (const def of customFieldDefs) {
      const group =
        map.get(def.categoryId) ??
        (() => {
          const created = {
            categoryId: def.categoryId,
            categoryLabel: def.categoryLabel,
            fields: [],
          };
          map.set(def.categoryId, created);
          return created;
        })();
      group.fields.push({
        key: def.key,
        label: def.label,
        fieldType: def.fieldType as CanonicalField["fieldType"],
        placeholder: def.placeholder,
      });
    }
    return [...map.values()];
  }, [customFieldDefs]);

  if (!orgId) {
    return (
      <div className="flex w-full items-center justify-center py-20">
        <p className="text-sm text-primary/60">
          Select an organisation to manage field presets.
        </p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex w-full items-center justify-center py-20">
        <Spinner className="size-6" />
      </div>
    );
  }

  const totalPresets = presets?.length ?? 0;

  // Org preset categories (canonical / defaultable) and the custom-field
  // section, built once so the tabs below can show them together ("All")
  // or in isolation.
  const orgSections = defaultableCategories.map((category) => (
    <div key={category.id} className="flex flex-col gap-4">
      <CategorySection
        category={category}
        presets={presetsByCategory.get(category.id) ?? []}
        favouriteSet={favouriteSet}
        orgId={typedOrgId}
      />
    </div>
  ));

  const customSection = (
    <>
      <NewCustomCategoryCard orgId={typedOrgId} />
      {customCategories.map((cc) => (
        <CustomCategorySection
          key={cc.categoryId}
          categoryId={cc.categoryId}
          categoryLabel={cc.categoryLabel}
          fields={cc.fields}
          presets={presetsByCategory.get(cc.categoryId) ?? []}
          favouriteSet={favouriteSet}
          orgId={typedOrgId}
          customFieldDefs={customFieldDefs ?? []}
        />
      ))}
    </>
  );

  // Per-case categories — populated automatically from each job's data, so
  // they're shown locked (read-only) but styled like the editable presets.
  // This tab is informational: practitioners look here to learn which keys
  // are available from job data, then edit the actual values on each job's
  // settings page. Firm-wide defaults live in Firm Presets or Custom Fields.
  const perCaseSections = perCaseCategories.map((category) => (
    <PerCaseCategorySection key={category.id} category={category} />
  ));

  return (
    <div className="flex w-full flex-col gap-6">
      {/* Header */}
      <div
        className={`flex items-start justify-between gap-6 p-6 ${GROUP_CARD_CLASS}`}
      >
        <div className="min-w-0 flex-1">
          <h3 className="inline-flex items-center gap-1 text-lg font-semibold text-primary">
            Field Presets
            <InfoTip side="bottom" className="translate-y-px">
              <span className="font-medium text-foreground">Preset</span>
              <span className="ml-1 text-muted-foreground">
                — a named bundle of values for one category, e.g. a Practitioner
                preset called &ldquo;Morgan Liquidsworth&rdquo; that fills in
                name, phone, registration number etc. in one click.
              </span>
            </InfoTip>
          </h3>
          <p className="mt-1 text-sm text-primary/60">
            Create named presets for your firm's information. Each preset groups
            all fields in a category together. You can have multiple presets per
            category — for example, different practitioners or office locations.
          </p>
        </div>
        <Badge variant="secondary" className="shrink-0">
          {totalPresets} {totalPresets === 1 ? "preset" : "presets"}
        </Badge>
      </div>

      <Tabs defaultValue="all">
        <TabsList>
          <TabsTrigger value="all">All</TabsTrigger>
          <TabsTrigger value="org">Firm Presets</TabsTrigger>
          <TabsTrigger value="custom">Custom Presets</TabsTrigger>
          <TabsTrigger value="per-case">Job Fields</TabsTrigger>
        </TabsList>

        <TabsContent value="all" className="flex flex-col gap-6">
          <SectionHeading
            icon={Building2}
            title="Firm Presets"
            subtitle="Built-in categories for your firm's information"
          />
          {orgSections}

          <SectionHeading
            icon={PlusCircle}
            title="Custom Presets"
            subtitle="Your own categories and fields"
          />
          {customSection}

          <SectionHeading
            icon={Lock}
            title="Job Fields"
            subtitle="Populated automatically from each job — read-only"
          />
          {perCaseSections}
        </TabsContent>

        <TabsContent value="org" className="flex flex-col gap-6">
          {orgSections}
        </TabsContent>

        <TabsContent value="custom" className="flex flex-col gap-6">
          {customSection}
        </TabsContent>

        <TabsContent value="per-case" className="flex flex-col gap-6">
          <div className="flex gap-3 rounded-lg border border-border/50 bg-secondary/30 px-4 py-3">
            <Lock className="mt-0.5 size-4 shrink-0 text-primary/30" />
            <p className="text-xs leading-relaxed text-primary/50">
              These fields fill automatically from each job&apos;s data — you
              can&apos;t set defaults here. Edit the actual values on each
              job&apos;s settings page. If you need a firm-wide default for
              something not derived from job data, create a{" "}
              <span className="font-medium">Custom Preset</span> instead.
            </p>
          </div>
          {perCaseSections}
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Category Section
// ---------------------------------------------------------------------------

function CategorySection({
  category,
  presets,
  favouriteSet,
  orgId,
}: {
  category: FieldCategory;
  presets: Array<Doc<"fieldPresets">>;
  favouriteSet: Set<Id<"fieldPresets">>;
  orgId: Id<"organizations">;
}) {
  const Icon = CATEGORY_ICONS[category.id] ?? FileText;
  const [showNewForm, setShowNewForm] = useState(false);
  // MOR-2342 — the firm-wide day-one contact default is a contact override,
  // kept collapsed behind a button so it doesn't crowd the Contact presets.
  const [showContactDefault, setShowContactDefault] = useState(false);

  const editableFields = useMemo(() => {
    const categoryFields = category.fields.filter(
      (f) =>
        f.fieldType !== "signature" &&
        f.fieldType !== "checkbox" &&
        !AUTO_ASSIGNED_FIELDS.has(f.key),
    );

    // Cross-category keys a preset may hold on top of its own fields: the
    // Gallagher form's values from other categories, and (MOR-2342) the
    // house-style practitioner / signatory titles on the Practitioner preset.
    const crossKeys = CROSS_CATEGORY_KEYS_BY_CATEGORY[category.id] ?? [];
    if (crossKeys.length === 0) return categoryFields;

    const crossFields: CanonicalField[] = crossKeys
      .map((key) => CANONICAL_FIELDS_MAP[key])
      .filter((f): f is CanonicalField & { categoryId: string } => !!f)
      .filter(
        (f) =>
          f.fieldType !== "signature" &&
          f.fieldType !== "checkbox" &&
          !AUTO_ASSIGNED_FIELDS.has(f.key),
      );

    return [...crossFields, ...categoryFields];
  }, [category]);

  return (
    <div className={GROUP_CARD_CLASS}>
      {/* Category header */}
      <div className="flex items-center justify-between border-b border-border/50 px-6 py-4">
        <div className="flex items-center gap-3">
          <div className="flex size-8 items-center justify-center rounded-lg bg-primary/5">
            <Icon className="size-4 text-primary/60" />
          </div>
          <div>
            <h4 className="text-sm font-semibold text-primary">
              {category.label}
            </h4>
            <p className="text-xs text-primary/40">{category.description}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-primary/40">
            {presets.length} {presets.length === 1 ? "preset" : "presets"}
          </span>
          {category.id === "contact" && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowContactDefault((prev) => !prev)}
              className="gap-1.5"
            >
              <ChevronDown
                className={cn(
                  "size-3.5 transition-transform",
                  showContactDefault && "rotate-180",
                )}
              />
              {showContactDefault ? "Hide" : "Set"} Primary Contact default
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowNewForm(true)}
            className="gap-1.5"
          >
            <Plus className="size-3.5" />
            Add Preset
          </Button>
        </div>
      </div>

      {/* Preset cards */}
      <div className="divide-y divide-border/30">
        {/* MOR-2342 — the firm-wide default for who the day-one contact
            fields come from. It overrides the Contact presets below, so it
            sits within this card but separate from the individual contacts. */}
        {category.id === "contact" && showContactDefault && (
          <div className="bg-secondary/20 px-6 py-5">
            <DayOneContactDefaultCard orgId={orgId} embedded />
          </div>
        )}
        {presets.map((preset) => (
          <PresetCard
            key={preset._id}
            preset={preset}
            fields={editableFields}
            isFavourite={favouriteSet.has(preset._id)}
            orgId={orgId}
          />
        ))}

        {presets.length === 0 && !showNewForm && (
          <div className="px-6 py-8 text-center">
            <p className="text-sm text-primary/40">
              No presets yet. Add one to start autofilling{" "}
              {category.label.toLowerCase()} fields.
            </p>
          </div>
        )}

        {showNewForm && (
          <NewPresetForm
            categoryId={category.id}
            categoryLabel={category.label}
            fields={editableFields}
            orgId={orgId}
            onClose={() => setShowNewForm(false)}
            isFirst={presets.length === 0}
          />
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Preset Card
// ---------------------------------------------------------------------------

function PresetCard({
  preset,
  fields,
  isFavourite,
  orgId,
}: {
  preset: Doc<"fieldPresets">;
  fields: CanonicalField[];
  isFavourite: boolean;
  orgId: Id<"organizations">;
}) {
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState(preset.name);
  const [editValues, setEditValues] = useState<Record<string, string>>(
    preset.values ?? {},
  );
  const [saving, setSaving] = useState(false);
  /**
   * Live title values for the appointment-type table. Kept in a ref as well
   * so `useAutosave` can read the latest draft on blur without waiting for
   * a re-render, matching the other editor surfaces.
   */
  const [titleValues, setTitleValues] = useState<Record<string, string>>(
    preset.values ?? {},
  );
  const titleValuesRef = useRef(titleValues);
  titleValuesRef.current = titleValues;

  const { mutateAsync: updatePreset } = useMutation({
    mutationFn: useConvexMutation(api.settings.precedents.presets.updatePreset),
  });
  const { mutateAsync: deletePreset } = useMutation({
    mutationFn: useConvexMutation(api.settings.precedents.presets.deletePreset),
  });
  const { mutateAsync: setDefault } = useMutation({
    mutationFn: useConvexMutation(api.settings.precedents.presets.setDefault),
  });
  const { mutateAsync: unsetDefault } = useMutation({
    mutationFn: useConvexMutation(api.settings.precedents.presets.unsetDefault),
  });
  const { mutateAsync: toggleFavourite } = useMutation({
    mutationFn: useConvexMutation(
      api.settings.precedents.favourites.toggleFavourite,
    ),
  });
  const titlesAutosave = useAutosave<Record<string, string>>({
    getValue: () => titleValuesRef.current,
    initialValue: preset.values ?? {},
    enabled: !editing,
    save: async (values) => {
      await updatePreset({
        presetId: preset._id,
        values,
      });
    },
  });
  const { mutateAsync: bulkSetPresetValues } = useMutation({
    mutationFn: useConvexMutation(
      api.settings.precedents.presets.bulkSetPresetValues,
    ),
  });

  /**
   * MOR-2354 — write one appointment type's titles to every practitioner
   * preset at once (blank inputs reset that type on every preset).
   */
  const handleApplyTitlesToAll = useCallback(
    async (patch: {
      appointmentType: string;
      set: Record<string, string>;
      clear: string[];
    }) => {
      try {
        const { updated } = await bulkSetPresetValues({
          orgId,
          categoryId: preset.categoryId,
          set: patch.set,
          clear: patch.clear,
        });
        const typeLabel =
          APPOINTMENT_TYPE_LABELS[patch.appointmentType as AppointmentType] ??
          patch.appointmentType;
        toast.success(
          `Applied ${typeLabel} titles to ${updated} practitioner preset${updated === 1 ? "" : "s"}`,
        );
      } catch (e) {
        toast.error(e instanceof Error ? e.message : "Failed to apply to all");
      }
    },
    [bulkSetPresetValues, orgId, preset.categoryId],
  );

  // Titles live in their own section, so exclude them from the plain grid
  // (and its count) to keep the two title concepts from appearing twice.
  const gridFields = fields.filter((f) => !isTitleField(f));
  const filledCount = gridFields.filter(
    (f) => (preset.values?.[f.key] ?? "").trim() !== "",
  ).length;

  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      await updatePreset({
        presetId: preset._id,
        name: editName.trim() || preset.name,
        values: editValues,
      });
      setTitleValues(editValues);
      titlesAutosave.resetBaseline(editValues);
      setEditing(false);
      toast.success("Preset updated");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to save");
    } finally {
      setSaving(false);
    }
  }, [
    preset._id,
    preset.name,
    editName,
    editValues,
    updatePreset,
    titlesAutosave,
  ]);

  const handleDelete = useCallback(async () => {
    try {
      await deletePreset({ presetId: preset._id });
      toast.success("Preset deleted");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to delete");
    }
  }, [preset._id, deletePreset]);

  const handleSetDefault = useCallback(async () => {
    try {
      await setDefault({ presetId: preset._id });
      toast.success("Set as default");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to set default");
    }
  }, [preset._id, setDefault]);

  const handleUnsetDefault = useCallback(async () => {
    try {
      await unsetDefault({ presetId: preset._id });
      toast.success(
        "Default removed — this category now has no default preset",
      );
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to remove default");
    }
  }, [preset._id, unsetDefault]);

  const handleToggleFavourite = useCallback(async () => {
    try {
      await toggleFavourite({ orgId, presetId: preset._id });
    } catch (e) {
      toast.error(
        e instanceof Error ? e.message : "Failed to toggle favourite",
      );
    }
  }, [orgId, preset._id, toggleFavourite]);

  const startEditing = useCallback(() => {
    setEditName(preset.name);
    setEditValues(titleValues);
    setEditing(true);
    setExpanded(true);
  }, [preset.name, titleValues]);

  const cancelEditing = useCallback(() => {
    setEditing(false);
    setEditName(preset.name);
    setEditValues(preset.values ?? {});
  }, [preset.name, preset.values]);

  return (
    <div className="group">
      {/* Preset header row */}
      <div className="flex items-center gap-3 px-6 py-3">
        <button
          type="button"
          onClick={() => !editing && setExpanded(!expanded)}
          className="flex flex-1 items-center gap-3 text-left"
        >
          {expanded ? (
            <ChevronDown className="size-3.5 shrink-0 text-primary/30" />
          ) : (
            <ChevronRight className="size-3.5 shrink-0 text-primary/30" />
          )}
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-primary">
                {preset.name}
              </span>
              {preset.isDefault && (
                <Badge variant="secondary" className="gap-1 text-[10px]">
                  <Shield className="size-2.5" />
                  Default
                </Badge>
              )}
            </div>
            <span className="text-xs text-primary/40">
              {filledCount}/{gridFields.length} fields filled
            </span>
          </div>
        </button>

        {/* Actions */}
        <div className="flex items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100">
          <button
            type="button"
            onClick={handleToggleFavourite}
            className="rounded-md p-1.5 transition hover:bg-secondary"
            title={isFavourite ? "Remove from favourites" : "Add to favourites"}
          >
            <Star
              className={cn(
                "size-3.5",
                isFavourite
                  ? "fill-amber-400 text-amber-400"
                  : "text-primary/30",
              )}
            />
          </button>

          {preset.isDefault ? (
            <button
              type="button"
              onClick={handleUnsetDefault}
              className="rounded-md p-1.5 transition hover:bg-secondary"
              title="Remove default (no preset will apply automatically)"
            >
              <ShieldOff className="size-3.5 text-primary/30" />
            </button>
          ) : (
            <button
              type="button"
              onClick={handleSetDefault}
              className="rounded-md p-1.5 transition hover:bg-secondary"
              title="Set as default"
            >
              <Shield className="size-3.5 text-primary/30" />
            </button>
          )}

          <button
            type="button"
            onClick={startEditing}
            className="rounded-md p-1.5 transition hover:bg-secondary"
            title="Edit preset"
          >
            <Pencil className="size-3.5 text-primary/30" />
          </button>

          <AlertDialog>
            <AlertDialogTrigger asChild>
              <button
                type="button"
                className="rounded-md p-1.5 transition hover:bg-destructive/10"
                title="Delete preset"
              >
                <Trash2 className="size-3.5 text-destructive/50" />
              </button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete preset</AlertDialogTitle>
                <AlertDialogDescription>
                  Are you sure you want to delete &ldquo;{preset.name}&rdquo;?
                  This action cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={handleDelete}>
                  Delete
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      {/* Expanded field list */}
      {expanded && (
        <div className="border-t border-border/20 bg-secondary/20 px-6 py-5">
          {editing ? (
            <div className="space-y-4">
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-medium text-primary/60">
                  Preset Name
                </label>
                <Input
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  placeholder="Name this preset..."
                  className="h-9 max-w-xs text-sm"
                />
              </div>
              {preset.categoryId === "gallagher" && <GallagherHelper />}
              <div className="grid gap-x-6 gap-y-4 sm:grid-cols-2">
                {gridFields.map((field) => (
                  <FieldInput
                    key={field.key}
                    field={field}
                    value={editValues[field.key] ?? ""}
                    onChange={(v) =>
                      setEditValues((prev) => ({ ...prev, [field.key]: v }))
                    }
                  />
                ))}
              </div>
              {preset.categoryId === "practitioner" && (
                <TitleByAppointmentType
                  values={editValues}
                  onChange={setEditValues}
                  defaultOpen
                  onApplyToAll={handleApplyTitlesToAll}
                  applyToAllLabel="Apply to all practitioner presets"
                />
              )}
              <div className="flex items-center gap-2 pt-2">
                <Button size="sm" onClick={handleSave} disabled={saving}>
                  {saving ? (
                    <Loader2 className="mr-1.5 size-3.5 animate-spin" />
                  ) : (
                    <Check className="mr-1.5 size-3.5" />
                  )}
                  Save
                </Button>
                <Button size="sm" variant="ghost" onClick={cancelEditing}>
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="grid gap-x-6 gap-y-3 sm:grid-cols-2">
                {gridFields.map((field) => {
                  const val = preset.values?.[field.key] ?? "";
                  return (
                    <div key={field.key} className="flex flex-col gap-0.5">
                      <span className="text-xs font-medium text-primary/50">
                        {field.label}
                      </span>
                      <span
                        className={cn(
                          "text-sm",
                          val ? "text-primary" : "italic text-primary/25",
                        )}
                      >
                        {val || "Not set"}
                      </span>
                    </div>
                  );
                })}
              </div>
              {preset.categoryId === "practitioner" && (
                <div className="space-y-3">
                  <div className="flex justify-end">
                    <AutosaveStatus
                      status={titlesAutosave.status}
                      lastSavedAt={titlesAutosave.lastSavedAt}
                      onRetry={titlesAutosave.retry}
                    />
                  </div>
                  <TitleByAppointmentType
                    values={titleValues}
                    onChange={setTitleValues}
                    onCommit={() => {
                      void titlesAutosave.flush();
                    }}
                    onApplyToAll={handleApplyTitlesToAll}
                    applyToAllLabel="Apply to all practitioner presets"
                  />
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// New Preset Form (inline)
// ---------------------------------------------------------------------------

function NewPresetForm({
  categoryId,
  categoryLabel,
  fields,
  orgId,
  onClose,
  isFirst,
}: {
  categoryId: string;
  categoryLabel: string;
  fields: CanonicalField[];
  orgId: Id<"organizations">;
  onClose: () => void;
  isFirst: boolean;
}) {
  const [name, setName] = useState("");
  const [values, setValues] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  const { mutateAsync: createPreset } = useMutation({
    mutationFn: useConvexMutation(api.settings.precedents.presets.createPreset),
  });

  const handleCreate = useCallback(async () => {
    if (!name.trim()) {
      toast.error("Please enter a name for this preset");
      return;
    }
    setSaving(true);
    try {
      await createPreset({
        orgId,
        categoryId,
        name: name.trim(),
        values,
        isDefault: isFirst,
      });
      toast.success(`Created "${name.trim()}" preset`);
      onClose();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to create preset");
    } finally {
      setSaving(false);
    }
  }, [orgId, categoryId, name, values, isFirst, createPreset, onClose]);

  return (
    <div className="border-t border-border/20 bg-primary/[0.02] px-6 py-5">
      <div className="mb-4 flex items-center justify-between">
        <h5 className="text-sm font-medium text-primary">
          New {categoryLabel} Preset
        </h5>
        <button
          type="button"
          onClick={onClose}
          className="rounded-md p-1 hover:bg-secondary"
        >
          <X className="size-4 text-primary/40" />
        </button>
      </div>

      <div className="space-y-4">
        <div className="flex flex-col gap-1.5">
          <label className="text-xs font-medium text-primary/60">
            Preset Name
          </label>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={`e.g. "Morgan Liquidsworth" or "Sydney Office"`}
            className="h-9 max-w-xs text-sm"
            autoFocus
          />
        </div>

        {categoryId === "gallagher" && <GallagherHelper />}

        <div className="grid gap-x-6 gap-y-4 sm:grid-cols-2">
          {fields
            .filter((field) => !isTitleField(field))
            .map((field) => (
              <FieldInput
                key={field.key}
                field={field}
                value={values[field.key] ?? ""}
                onChange={(v) =>
                  setValues((prev) => ({ ...prev, [field.key]: v }))
                }
              />
            ))}
        </div>
        {categoryId === "practitioner" && (
          <TitleByAppointmentType values={values} onChange={setValues} />
        )}

        <div className="flex items-center gap-2 pt-2">
          <Button
            size="sm"
            onClick={handleCreate}
            disabled={saving || !name.trim()}
          >
            {saving ? (
              <Loader2 className="mr-1.5 size-3.5 animate-spin" />
            ) : (
              <Plus className="mr-1.5 size-3.5" />
            )}
            Create Preset
          </Button>
          <Button size="sm" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Gallagher Helper
// ---------------------------------------------------------------------------

function GallagherHelper() {
  return (
    <div className="flex gap-3 rounded-lg border border-border/50 bg-secondary/30 px-4 py-3">
      <Info className="mt-0.5 size-4 shrink-0 text-primary/30" />
      <div className="space-y-1 text-xs text-primary/50">
        <p className="font-medium text-primary/60">
          How defaults work on this form
        </p>
        <ul className="list-disc space-y-0.5 pl-4">
          <li>
            <span className="text-primary/60">Pre-filled values</span> (e.g.
            &ldquo;Unknown&rdquo;, &ldquo;TBA&rdquo;, &ldquo;No&rdquo;) are used
            on every new notification unless you change them.
          </li>
          <li>
            <span className="text-primary/60">Empty fields</span> are filled
            automatically from job data and your Practitioner / Firm / Contact
            presets.
          </li>
          <li>
            <span className="text-primary/60">Override any field</span> by
            typing a value &mdash; it will replace the automatic value. Clear it
            to go back to automatic.
          </li>
        </ul>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Field Input (type-aware: checkbox renders as switch, others as text/email)
// ---------------------------------------------------------------------------

function FieldInput({
  field,
  value,
  onChange,
}: {
  field: CanonicalField;
  value: string;
  onChange: (value: string) => void;
}) {
  const isCheckbox = field.fieldType === "checkbox";
  const checked = value === "true" || value === "yes" || value === "1";
  const hasValue = isCheckbox ? checked : value.trim() !== "";

  if (isCheckbox) {
    return (
      <div className="flex items-center justify-between rounded-md border border-border px-3 py-2">
        <label
          htmlFor={`field-${field.key}`}
          className="text-xs font-medium text-primary/60"
        >
          {field.label}
        </label>
        <Switch
          id={`field-${field.key}`}
          checked={checked}
          onCheckedChange={(c) => onChange(c ? "true" : "false")}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      <div className="mb-1.5 flex items-baseline justify-between gap-2">
        <label
          htmlFor={`field-${field.key}`}
          className="text-xs font-medium text-primary/60"
        >
          {field.label}
        </label>
        {hasValue && (
          <span className="text-[10px] font-medium text-emerald-600">Set</span>
        )}
      </div>
      <Input
        id={`field-${field.key}`}
        type={field.fieldType === "email" ? "email" : "text"}
        placeholder=""
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={cn(
          "h-9 text-sm transition",
          hasValue &&
            "border-emerald-200 bg-emerald-50/30 dark:border-emerald-900/30 dark:bg-emerald-950/10",
        )}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Custom Category Section
// ---------------------------------------------------------------------------

function CustomCategorySection({
  categoryId,
  categoryLabel,
  fields,
  presets,
  favouriteSet,
  orgId,
  customFieldDefs,
}: {
  categoryId: string;
  categoryLabel: string;
  fields: CanonicalField[];
  presets: Array<Doc<"fieldPresets">>;
  favouriteSet: Set<Id<"fieldPresets">>;
  orgId: Id<"organizations">;
  customFieldDefs: Array<Doc<"customFieldDefinitions">>;
}) {
  const [showNewForm, setShowNewForm] = useState(false);
  const [showManage, setShowManage] = useState(false);

  const { mutateAsync: deleteField } = useMutation({
    mutationFn: useConvexMutation(
      api.settings.precedents.customFields.deleteField,
    ),
  });

  const { mutateAsync: createField } = useMutation({
    mutationFn: useConvexMutation(
      api.settings.precedents.customFields.createField,
    ),
  });

  const { mutateAsync: updateField } = useMutation({
    mutationFn: useConvexMutation(
      api.settings.precedents.customFields.updateField,
    ),
  });

  const { mutateAsync: deleteCategory } = useMutation({
    mutationFn: useConvexMutation(
      api.settings.precedents.customFields.deleteCategory,
    ),
  });

  const categoryDefs = useMemo(
    () => customFieldDefs.filter((d) => d.categoryId === categoryId),
    [customFieldDefs, categoryId],
  );

  const [newLabel, setNewLabel] = useState("");
  const [editingCategory, setEditingCategory] = useState(false);
  const [categoryDraft, setCategoryDraft] = useState(categoryLabel);

  const handleRenameCategory = useCallback(async () => {
    const trimmed = categoryDraft.trim();
    if (!trimmed || trimmed === categoryLabel) {
      setEditingCategory(false);
      setCategoryDraft(categoryLabel);
      return;
    }
    const anchor = categoryDefs[0];
    if (!anchor) {
      setEditingCategory(false);
      return;
    }
    try {
      await updateField({ fieldId: anchor._id, categoryLabel: trimmed });
      toast.success("Category renamed");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to rename category");
      setCategoryDraft(categoryLabel);
    }
    setEditingCategory(false);
  }, [categoryDraft, categoryLabel, categoryDefs, updateField]);

  const handleRenameField = useCallback(
    async (
      fieldId: Id<"customFieldDefinitions">,
      nextLabel: string,
      currentLabel: string,
    ) => {
      const trimmed = nextLabel.trim();
      if (!trimmed || trimmed === currentLabel) return;
      try {
        await updateField({ fieldId, label: trimmed });
        toast.success("Field renamed");
      } catch (e) {
        toast.error(e instanceof Error ? e.message : "Failed to rename field");
      }
    },
    [updateField],
  );

  const handleAddField = useCallback(async () => {
    if (!newLabel.trim()) return;
    try {
      await createField({
        orgId,
        categoryId,
        categoryLabel,
        key: newLabel
          .trim()
          .toLowerCase()
          .replace(/[^a-z0-9\s_]/g, "")
          .replace(/\s+/g, "_"),
        label: newLabel.trim(),
        fieldType: "text",
      });
      setNewLabel("");
      toast.success("Field added");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to add field");
    }
  }, [orgId, categoryId, categoryLabel, newLabel, createField]);

  const handleDeleteField = useCallback(
    async (fieldId: Id<"customFieldDefinitions">) => {
      try {
        await deleteField({ fieldId });
        toast.success("Field removed");
      } catch (e) {
        toast.error(e instanceof Error ? e.message : "Failed to remove field");
      }
    },
    [deleteField],
  );

  const handleDeleteCategory = useCallback(async () => {
    try {
      const result = await deleteCategory({ orgId, categoryId });
      const fieldNoun = result.deletedFields === 1 ? "field" : "fields";
      const presetNoun = result.deletedPresets === 1 ? "preset" : "presets";
      toast.success(
        `Removed "${categoryLabel}" (${result.deletedFields} ${fieldNoun}, ${result.deletedPresets} ${presetNoun})`,
      );
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to delete category");
    }
  }, [orgId, categoryId, categoryLabel, deleteCategory]);

  return (
    <div className={GROUP_CARD_CLASS}>
      {/* Category header */}
      <div className="flex items-center justify-between border-b border-border/50 px-6 py-4">
        <div className="flex items-center gap-3">
          <div className="flex size-8 items-center justify-center rounded-lg bg-violet-500/10">
            <FileText className="size-4 text-violet-600/60" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              {editingCategory ? (
                <Input
                  value={categoryDraft}
                  onChange={(e) => setCategoryDraft(e.target.value)}
                  onBlur={handleRenameCategory}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleRenameCategory();
                    if (e.key === "Escape") {
                      setCategoryDraft(categoryLabel);
                      setEditingCategory(false);
                    }
                  }}
                  className="h-7 w-56 text-sm font-semibold"
                  autoFocus
                />
              ) : (
                <button
                  type="button"
                  onClick={() => {
                    setCategoryDraft(categoryLabel);
                    setEditingCategory(true);
                  }}
                  className="text-sm font-semibold text-primary transition hover:text-primary/80"
                  title="Click to rename category"
                >
                  {categoryLabel}
                </button>
              )}
              <Badge variant="secondary" className="text-[10px]">
                Custom
              </Badge>
            </div>
            <p className="text-xs text-primary/40">
              {fields.length} custom field{fields.length !== 1 ? "s" : ""}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Dialog open={showManage} onOpenChange={setShowManage}>
            <DialogTrigger asChild>
              <Button variant="ghost" size="sm" className="gap-1.5 text-xs">
                <Pencil className="size-3" />
                Manage Fields
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogTitle>Manage {categoryLabel}</DialogTitle>
              <div className="space-y-3 py-2">
                <div className="space-y-2 rounded-md border border-dashed border-border/50 p-3">
                  <Label className="inline-flex items-center gap-1 text-xs">
                    Add New Field
                    <InfoTip side="top">
                      <span className="font-medium text-foreground">Field</span>
                      <span className="ml-1 text-muted-foreground">
                        — a single piece of information stored on a preset, e.g.
                        &ldquo;Policy Number&rdquo; under an Insurance Details
                        category.
                      </span>
                    </InfoTip>
                  </Label>
                  <div className="flex gap-2">
                    <Input
                      value={newLabel}
                      onChange={(e) => setNewLabel(e.target.value)}
                      placeholder="Field label"
                      className="h-8 text-sm"
                    />
                    <Button
                      size="sm"
                      className="h-8 shrink-0 gap-1.5"
                      disabled={!newLabel.trim()}
                      onClick={handleAddField}
                    >
                      <Plus className="size-3.5" />
                      Add
                    </Button>
                  </div>
                </div>

                {categoryDefs.map((def) => (
                  <ManageFieldRow
                    key={def._id}
                    def={def}
                    onRename={handleRenameField}
                    onDelete={handleDeleteField}
                  />
                ))}
              </div>
            </DialogContent>
          </Dialog>

          <span className="text-xs font-medium text-primary/40">
            {presets.length} {presets.length === 1 ? "preset" : "presets"}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowNewForm(true)}
            className="gap-1.5"
          >
            <Plus className="size-3.5" />
            Add Preset
          </Button>

          <AlertDialog>
            <AlertDialogTrigger asChild>
              <button
                type="button"
                className="rounded-md p-1.5 transition hover:bg-destructive/10"
                title="Delete category"
                aria-label={`Delete ${categoryLabel} category`}
              >
                <Trash2 className="size-3.5 text-destructive/60" />
              </button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>
                  Delete &ldquo;{categoryLabel}&rdquo;?
                </AlertDialogTitle>
                <AlertDialogDescription asChild>
                  <div className="space-y-2">
                    <p>This will permanently remove:</p>
                    <ul className="list-disc space-y-0.5 pl-5">
                      <li>
                        {fields.length} custom{" "}
                        {fields.length === 1 ? "field" : "fields"}
                      </li>
                      <li>
                        {presets.length}{" "}
                        {presets.length === 1 ? "preset" : "presets"} in this
                        category
                      </li>
                    </ul>
                    <p className="text-destructive">
                      This action cannot be undone.
                    </p>
                  </div>
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleDeleteCategory}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                >
                  Delete category
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      {/* Preset cards */}
      <div className="divide-y divide-border/30">
        {presets.map((preset) => (
          <PresetCard
            key={preset._id}
            preset={preset}
            fields={fields}
            isFavourite={favouriteSet.has(preset._id)}
            orgId={orgId}
          />
        ))}

        {presets.length === 0 && !showNewForm && (
          <div className="px-6 py-8 text-center">
            <p className="text-sm text-primary/40">
              No presets yet. Add one to start autofilling{" "}
              {categoryLabel.toLowerCase()} fields.
            </p>
          </div>
        )}

        {showNewForm && (
          <NewPresetForm
            categoryId={categoryId}
            categoryLabel={categoryLabel}
            fields={fields}
            orgId={orgId}
            onClose={() => setShowNewForm(false)}
            isFirst={presets.length === 0}
          />
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Manage Field Row (inline rename + delete)
// ---------------------------------------------------------------------------

function ManageFieldRow({
  def,
  onRename,
  onDelete,
}: {
  def: Doc<"customFieldDefinitions">;
  onRename: (
    fieldId: Id<"customFieldDefinitions">,
    nextLabel: string,
    currentLabel: string,
  ) => Promise<void>;
  onDelete: (fieldId: Id<"customFieldDefinitions">) => Promise<void>;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<string>(def.label);

  const keyDiffers =
    def.key !==
    def.label
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9\s_]/g, "")
      .replace(/\s+/g, "_");

  const commit = useCallback(async () => {
    const trimmed = draft.trim();
    if (!trimmed || trimmed === def.label) {
      setDraft(def.label);
      setEditing(false);
      return;
    }
    await onRename(def._id, trimmed, def.label);
    setEditing(false);
  }, [draft, def._id, def.label, onRename]);

  return (
    <div className="flex items-start justify-between gap-2 rounded-md border border-border px-3 py-2">
      <div className="flex min-w-0 flex-1 items-start gap-2">
        {editing ? (
          <Input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => {
              if (e.key === "Enter") commit();
              if (e.key === "Escape") {
                setDraft(def.label);
                setEditing(false);
              }
            }}
            className="h-7 text-sm"
            autoFocus
          />
        ) : (
          <>
            <button
              type="button"
              onClick={() => {
                setDraft(def.label);
                setEditing(true);
              }}
              className="min-w-0 flex-1 break-words text-left text-sm font-medium text-primary transition [overflow-wrap:anywhere] hover:text-primary/80"
              title="Click to rename field"
            >
              {def.label}
            </button>
            {keyDiffers && (
              <TooltipProvider delayDuration={150}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      className="mt-0.5 shrink-0 rounded-full p-0.5 text-muted-foreground/60 transition hover:text-muted-foreground"
                      aria-label="Show original field identifier"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Info className="size-3" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="text-xs">
                    <span className="text-[10px] uppercase tracking-wide opacity-70">
                      Original key
                    </span>
                    <div className="font-mono">{def.key}</div>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </>
        )}
      </div>
      <div className="flex shrink-0 items-center gap-1">
        {!editing && (
          <button
            type="button"
            onClick={() => {
              setDraft(def.label);
              setEditing(true);
            }}
            className="rounded p-1 text-primary/40 transition hover:bg-secondary hover:text-primary/70"
            title="Rename field"
          >
            <Pencil className="size-3.5" />
          </button>
        )}
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <button
              type="button"
              className="rounded p-1 hover:bg-destructive/10"
            >
              <Trash2 className="size-3.5 text-destructive/50" />
            </button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Remove field</AlertDialogTitle>
              <AlertDialogDescription>
                Remove &ldquo;{def.label}&rdquo; from this category? Existing
                presets will keep their values but the field won&apos;t appear
                in new presets.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={() => onDelete(def._id)}>
                Remove
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// New Custom Category Card
// ---------------------------------------------------------------------------

function NewCustomCategoryCard({ orgId }: { orgId: Id<"organizations"> }) {
  const [open, setOpen] = useState(false);
  const [categoryName, setCategoryName] = useState("");
  const [fieldLabel, setFieldLabel] = useState("");
  const [saving, setSaving] = useState(false);

  const { mutateAsync: createField } = useMutation({
    mutationFn: useConvexMutation(
      api.settings.precedents.customFields.createField,
    ),
  });

  const slugify = (s: string) =>
    s
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9\s_]/g, "")
      .replace(/\s+/g, "_");

  const handleCreate = useCallback(async () => {
    if (!categoryName.trim() || !fieldLabel.trim()) return;
    setSaving(true);
    try {
      await createField({
        orgId,
        categoryId: slugify(categoryName),
        categoryLabel: categoryName.trim(),
        key: slugify(fieldLabel),
        label: fieldLabel.trim(),
        fieldType: "text",
      });
      toast.success(
        `Created "${categoryName.trim()}" with field "${fieldLabel.trim()}"`,
      );
      setCategoryName("");
      setFieldLabel("");
      setOpen(false);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to create category");
    } finally {
      setSaving(false);
    }
  }, [orgId, categoryName, fieldLabel, createField]);

  const reset = useCallback(() => {
    setOpen(false);
    setCategoryName("");
    setFieldLabel("");
  }, []);

  if (!open) {
    return (
      <div
        className={cn(
          GROUP_CARD_CLASS,
          "flex w-full items-center border border-dashed border-border/40",
        )}
      >
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="flex flex-1 items-center gap-3 px-6 py-4 text-sm text-primary/40 hover:text-primary/60"
        >
          <div className="flex size-8 items-center justify-center rounded-lg bg-primary/5">
            <PlusCircle className="size-4" />
          </div>
          New Custom Category
        </button>
        <div className="pr-4">
          <InfoTip side="left" align="center">
            <CustomFieldsHelp />
          </InfoTip>
        </div>
      </div>
    );
  }

  return (
    <div className={GROUP_CARD_CLASS}>
      <div className="px-6 py-5">
        <div className="mb-4 flex items-center justify-between">
          <h5 className="inline-flex items-center gap-1 text-sm font-semibold text-primary">
            New Custom Category
            <InfoTip side="top">
              <CustomFieldsHelp />
            </InfoTip>
          </h5>
          <button
            type="button"
            onClick={reset}
            className="rounded-md p-1 hover:bg-secondary"
          >
            <X className="size-4 text-primary/40" />
          </button>
        </div>

        <p className="mb-4 text-xs text-primary/50">
          Create a new category of custom fields. Add the first field now and
          more via &ldquo;Manage Fields&rdquo; afterwards.
        </p>

        <div className="space-y-3">
          <div>
            <Label className="text-xs">Category Name</Label>
            <Input
              value={categoryName}
              onChange={(e) => setCategoryName(e.target.value)}
              placeholder='e.g. "Insurance Details" or "Court Information"'
              className="mt-1 h-9 text-sm"
              autoFocus
            />
          </div>

          <div className="rounded-md border border-dashed border-border/50 bg-secondary/20 p-3">
            <Label className="text-xs text-primary/60">First field</Label>
            <Input
              value={fieldLabel}
              onChange={(e) => setFieldLabel(e.target.value)}
              placeholder="Field label"
              className="mt-1.5 h-8 text-sm"
            />
            {fieldLabel && (
              <span className="mt-1 block font-mono text-[10px] text-muted-foreground">
                key: {slugify(fieldLabel)}
              </span>
            )}
          </div>

          <div className="flex items-center gap-2 pt-1">
            <Button
              size="sm"
              onClick={handleCreate}
              disabled={saving || !categoryName.trim() || !fieldLabel.trim()}
            >
              {saving ? (
                <Loader2 className="mr-1.5 size-3.5 animate-spin" />
              ) : (
                <Plus className="mr-1.5 size-3.5" />
              )}
              Create Category
            </Button>
            <Button size="sm" variant="ghost" onClick={reset}>
              Cancel
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Per-Case Category Section (locked — styled like an editable category)
// ---------------------------------------------------------------------------

/**
 * Read-only display of a per-case category — shows practitioners which
 * field keys the system pulls from each job (so they know what
 * placeholders are available when authoring templates) without offering
 * an editor. The actual values are managed on each job's settings page.
 * For firm-wide defaults that don't vary per case, use Custom Presets
 * instead.
 */
function PerCaseCategorySection({ category }: { category: FieldCategory }) {
  const Icon = CATEGORY_ICONS[category.id] ?? FileText;
  const [expanded, setExpanded] = useState(false);

  return (
    <div className={GROUP_CARD_CLASS}>
      {/* Category header */}
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="flex w-full items-center justify-between border-b border-border/50 px-6 py-4 text-left"
      >
        <div className="flex items-center gap-3">
          <div className="flex size-8 items-center justify-center rounded-lg bg-primary/5">
            <Icon className="size-4 text-primary/60" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h4 className="text-sm font-semibold text-primary">
                {category.label}
              </h4>
              <Badge variant="secondary" className="gap-1 text-[10px]">
                <Lock className="size-2.5" />
                Locked
              </Badge>
            </div>
            <p className="text-xs text-primary/40">{category.description}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-primary/40">
            {category.fields.length}{" "}
            {category.fields.length === 1 ? "field" : "fields"}
          </span>
          {expanded ? (
            <ChevronDown className="size-3.5 text-primary/30" />
          ) : (
            <ChevronRight className="size-3.5 text-primary/30" />
          )}
        </div>
      </button>

      {/* Read-only field list */}
      {expanded && (
        <div className="bg-secondary/20 px-6 py-5">
          <div className="grid gap-x-6 gap-y-3 sm:grid-cols-2">
            {category.fields.map((field) => (
              <div
                key={field.key}
                className="flex flex-col gap-0.5"
                title={field.description}
              >
                <span className="text-xs font-medium text-primary/50">
                  {field.label}
                </span>
                <span className="inline-flex items-center gap-1 text-sm italic text-primary/25">
                  <Lock className="size-3" />
                  Filled from job data
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default FieldsSettings;
